import React, { Suspense, useCallback, useMemo } from 'react';

import { WidgetButton } from '@interlayer/js/app/components/main/FeaturedBanner/components/WidgetButton';
import { FilterTabs } from '@interlayer/js/app/components/main/FilterTabs';

import { Skeleton } from 'js/app/components/main/Skeleton';

import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useNavigate } from '@interlayer/js/hooks/navigate';

import { useGameMenuItems } from 'js/app/components/main/Games/components/ControlPanel/hooks/useGameMenuItems';
import { useNavigateToCategoryPath } from 'js/app/components/main/Games/components/ControlPanel/hooks/useNavigateToCategoryPath';
import { GAME_EVENTS } from 'js/app/components/main/Games/constants';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGameEventDispatch } from 'js/app/components/main/Games/hooks/useGameEventDispatch';
import { selectActiveCategory } from 'js/app/components/main/Games/store/actions';
import { useGamesDispatch, useGamesSelector } from 'js/app/components/main/Games/store/context';
import { SKELETON_TYPES } from 'js/app/components/main/Skeleton/constants';

import style from './ControlPanel.scss';

export const ControlPanel = () => {
    const dispatch = useGamesDispatch();
    const gameMenuItems = useGameMenuItems();
    const { activeCategory } = useGamesSelector();
    const gameEventDispatch = useGameEventDispatch();
    const { enableCtaButton, ctaButton, enableListOfCategories } = useGameComponentConfig();
    const navigateToPathCategory = useNavigateToCategoryPath();
    const navigate = useNavigate();

    const handleCategoryClick = useCallback((categoryName) => {
        dispatch(selectActiveCategory(categoryName));
        navigateToPathCategory(categoryName);

        gameEventDispatch({
            name: GAME_EVENTS.GAME_PORTLET_CATEGORY_CHANGE,
            data: { categoryName }
        });
    }, [dispatch, gameEventDispatch, navigateToPathCategory]);

    const categoryNamesList = useMemo(() => {
        return gameMenuItems.map((item) => item.name);
    }, [gameMenuItems]);

    const handleSetSelectedTag = useCallback((name) => {
        const gameMenuItem = gameMenuItems.find((item) => item.name === name);

        if (gameMenuItem.isSubPage) {
            navigate(gameMenuItem.url);
            return;
        }

        handleCategoryClick(name);
    }, [gameMenuItems, handleCategoryClick, navigate]);

    return (
        <div className={style.panel}>
            {activeCategory.name
                ? (
                    <div className={style.container}>
                        {enableListOfCategories && (
                            <Suspense fallback="">
                                <FilterTabs
                                    classNames={{
                                        wrapper: style.wrapper,
                                        mainTag: style.mainTag,
                                        tagsWrapper: style.tagsWrapper,
                                        mainTagText: style.mainTagText,
                                        mainTagWrapper: style.mainTagWrapper
                                    }}
                                    tags={categoryNamesList}
                                    selectedTag={activeCategory.name}
                                    setSelectedTag={handleSetSelectedTag}
                                />
                            </Suspense>
                        )}

                        {enableCtaButton && (
                            <WidgetButton
                                widgetButtons={[ctaButton]}
                                classNames={{
                                    button: clsx(style.ctaButton, { [style.isOnlyButtonDisplayed]: !enableListOfCategories })
                                }}
                            />
                        )}
                    </div>
                )
                : (
                    <Skeleton configuration={[{ type: SKELETON_TYPES.inPageMenuItem, count: 5 }]} />
                )
            }
        </div>
    );
};
